import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC, type JSX } from 'react';
import Button from '../Button/Button';
import Conditional from '../Conditional/Conditional';

type TSharedProps = {
  message?: string | JSX.Element;
  /**
   * How the toast can be dismissed early, before its auto-hide timer runs out:
   * - `'click'` — clicking anywhere on the toast dismisses it. Use for toasts
   *   whose message never needs to be selected/copied.
   * - `'button'` — only an explicit close button dismisses it, so the message
   *   text stays selectable. Use when the message contains copyable content.
   * - `false` (default) — not dismissible early at all.
   */
  dismissible?: 'click' | 'button' | false;
  onDismiss?: () => void;
};

export type ToastProps =
  | ({
      variant: 'soft' | 'solid';
      colour: 'black' | 'gray' | 'green' | 'blue' | 'red' | 'yellow';
    } & TSharedProps)
  | ({
      variant: 'white';
      icon?:
        | 'success'
        | 'error'
        | 'warning'
        | 'info'
        | { iconName: TIconName; className?: string };
    } & TSharedProps);

const Toast: FC<ToastProps> = (props) => {
  if (props.variant === 'white') {
    const { message, icon, dismissible = false, onDismiss } = props;
    return (
      <div
        className={clstx(
          'typography:prose z-toast rounded-xl border border-gray-200 bg-white shadow-lg',
          dismissible === 'click' && 'cursor-pointer'
        )}
        role="alert"
        // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-live#polite
        aria-live="polite"
        onClick={dismissible === 'click' ? onDismiss : undefined}
      >
        <div className="flex gap-2 p-4 items-center">
          {icon && (
            <UntitledIcon
              name={(() => {
                switch (icon) {
                  case 'success':
                    return 'check-circle.3';
                  case 'error':
                    return 'x-circle.3';
                  case 'warning':
                    return 'info-octagon.3';
                  case 'info':
                    return 'info-circle.3';
                  default:
                    return icon.iconName;
                }
              })()}
              className={clstx(
                'h-5 w-5 shrink-0',
                icon === 'success' && 'text-green-500',
                icon === 'error' && 'text-red-500',
                icon === 'warning' && 'text-yellow-600',
                icon === 'info' && 'text-blue-500',
                typeof icon !== 'string' && icon.className
              )}
            />
          )}

          <Conditional condition={!!message}>
            {typeof message === 'string' ? (
              <p>{message}</p>
            ) : (
              <div>{message}</div>
            )}
          </Conditional>

          <Conditional condition={dismissible === 'button'}>
            <Button
              className="ml-auto"
              variant="quaternary"
              size={'xs'}
              onClick={(e) => {
                e.stopPropagation();
                onDismiss?.();
              }}
            >
              <span className="sr-only">Dismiss</span>
              <UntitledIcon name="x-close" className="h-3 w-3" />
            </Button>
          </Conditional>
        </div>
      </div>
    );
  }

  const {
    variant = 'solid',
    message,
    colour = 'black',
    dismissible = false,
    onDismiss,
  } = props;

  return (
    <div
      className={clstx(
        'typography:prose rounded-xl text-white shadow-lg',
        // Solid
        variant === 'solid' && colour === 'black' && 'bg-gray-800',
        variant === 'solid' && colour === 'gray' && 'bg-gray-500',
        variant === 'solid' && colour === 'green' && 'bg-green-500',
        variant === 'solid' && colour === 'blue' && 'bg-blue-500',
        variant === 'solid' && colour === 'red' && 'bg-red-500',
        variant === 'solid' && colour === 'yellow' && 'bg-yellow-500',
        // Soft
        variant === 'soft' &&
          colour === 'black' &&
          'border border-gray-200 bg-gray-100 text-gray-800',
        variant === 'soft' &&
          colour === 'gray' &&
          'border border-gray-200 bg-gray-50 text-gray-600',
        variant === 'soft' &&
          colour === 'green' &&
          'border border-green-200 bg-green-100 text-green-800',
        variant === 'soft' &&
          colour === 'blue' &&
          'border border-blue-200 bg-blue-100 text-blue-800',
        variant === 'soft' &&
          colour === 'red' &&
          'border border-red-200 bg-red-100 text-red-800',
        variant === 'soft' &&
          colour === 'yellow' &&
          'border border-yellow-200 bg-yellow-100 text-yellow-800',
        dismissible === 'click' && 'cursor-pointer'
      )}
      role="alert"
      onClick={dismissible === 'click' ? onDismiss : undefined}
    >
      <div className="flex p-4">
        <p>{message}</p>

        <div className="ms-auto">
          <Conditional condition={dismissible === 'button'}>
            <Button
              variant="quaternary"
              className={variant === 'solid' ? 'text-white' : undefined}
              size={'xs'}
              onClick={(e) => {
                e.stopPropagation();
                onDismiss?.();
              }}
            >
              <span className="sr-only">Dismiss</span>
              <UntitledIcon name="x-close" className="h-3 w-3" />
            </Button>
          </Conditional>
        </div>
      </div>
    </div>
  );
};

export default Toast;
